import type { Analysis, Facts, Overrides, Result, Setup, Survey, Target } from './engine'
import type { LoadStage, WorkerRequest, WorkerResponse } from './protocol'

type Pending = (response: Result<unknown>) => void
type Request = WorkerRequest extends infer R ? (R extends WorkerRequest ? Omit<R, 'id'> : never) : never

const failure = (code: string): Result<never> => ({ ok: false, error: { code, field: null, message: '' } })

/** Main-thread handle to the anonymization worker. Sources are transferred, not copied. */
export class AnonymizationClient {
  private readonly worker = new Worker(new URL('./engine.worker.ts', import.meta.url), { type: 'module' })
  private readonly pending = new Map<number, Pending>()
  private nextId = 0
  /** The worker crashed or was closed; it answers nothing more, so a new client is needed. */
  broken = false

  constructor(onStage: (stage: LoadStage) => void) {
    this.worker.onmessage = ({ data }: MessageEvent<WorkerResponse>) => {
      if ('progress' in data) return onStage(data.progress)
      this.pending.get(data.id)?.(data.ok ? { ok: true, value: data.value } : { ok: false, error: data.error })
      this.pending.delete(data.id)
    }
    // For example out of memory on a very large file: fail what waits instead of hanging.
    this.worker.onerror = this.worker.onmessageerror = () => this.fail('worker_failed')
  }

  /** Keeps the token inside the worker and returns its key ID; an empty token clears it (UPL-18). */
  async configure(setup: Setup): Promise<string | null> {
    const result = await this.send({ type: 'configure', setup })
    if (!result.ok) throw new Error(result.error.code)
    return (result.value as string | undefined) ?? null
  }

  pseudonym(setup: Setup, patientId: string): Promise<Result<string>> {
    return this.send({ type: 'pseudonym', setup, patientId }) as Promise<Result<string>>
  }

  survey(source: ArrayBuffer): Promise<Result<Survey>> {
    return this.send({ type: 'survey', source }, [source]) as Promise<Result<Survey>>
  }

  pixelKey(source: ArrayBuffer): Promise<Result<string>> {
    return this.send({ type: 'pixels', source }, [source]) as Promise<Result<string>>
  }

  analyze(source: ArrayBuffer, overrides?: Overrides, present?: string[]): Promise<Result<Analysis>> {
    return this.send({ type: 'analyze', source, overrides, present }, [source]) as Promise<Result<Analysis>>
  }

  process(source: ArrayBuffer, overrides?: Overrides, targets?: Target<ArrayBuffer>[]): Promise<Result<{ data: Uint8Array; facts: Facts }>> {
    const transfer = [source, ...(targets ?? []).map((target) => target.source)]
    return this.send({ type: 'process', source, overrides, targets }, transfer) as Promise<Result<{ data: Uint8Array; facts: Facts }>>
  }

  viewable(source: ArrayBuffer): Promise<Result<Uint8Array>> {
    return this.send({ type: 'viewable', source }, [source]) as Promise<Result<Uint8Array>>
  }

  /** Ends the worker, discarding the token and everything in its memory. */
  close(): void {
    this.worker.terminate()
    this.fail('cancelled')
  }

  private fail(code: string): void {
    this.broken = true
    for (const resolve of this.pending.values()) resolve(failure(code))
    this.pending.clear()
  }

  private send(request: Request, transfer: Transferable[] = []): Promise<Result<unknown>> {
    if (this.broken) return Promise.resolve(failure('worker_failed'))
    const id = this.nextId++
    return new Promise((resolve) => {
      this.pending.set(id, resolve)
      this.worker.postMessage({ ...request, id }, transfer)
    })
  }
}

/**
 * Patients' pseudonyms (TOK-21, TOK-25, AUD-05) from their patient IDs, from an engine of its own that
 * is closed afterwards, so the token leaves with it. The slow hash takes about half a second each,
 * after the engine has loaded.
 */
export async function derivePseudonyms(setup: Setup, patientIds: readonly string[]): Promise<string[]> {
  const client = new AnonymizationClient(() => {})
  try {
    const pseudonyms: string[] = []
    for (const patientId of patientIds) {
      const result = await client.pseudonym(setup, patientId)
      if (!result.ok) throw new Error(result.error.code)
      pseudonyms.push(result.value)
    }
    return pseudonyms
  } finally {
    client.close()
  }
}

/**
 * The duplicate keys of a patient's archive images (TOK-21), from an engine of its own that needs no
 * token, and how many files gave none, such as files that are not DICOM images. Only the keys leave
 * the browser.
 */
/**
 * What a site check counts of each original (TOK-30), from an engine of its own that needs no token,
 * and how many files gave nothing, such as files that are not DICOM images. Nothing leaves the browser.
 */
export async function surveyFiles(files: readonly File[], onProgress?: (done: number) => void): Promise<{ surveys: Survey[]; failed: number }> {
  const client = new AnonymizationClient(() => {})
  try {
    const surveys: Survey[] = []
    let failed = 0
    for (const file of files) {
      const result = await client.survey(await file.arrayBuffer())
      if (result.ok) surveys.push(result.value)
      else failed += 1
      onProgress?.(surveys.length + failed)
    }
    return { surveys, failed }
  } finally {
    client.close()
  }
}

export async function pixelKeys(files: readonly File[]): Promise<{ keys: string[]; failed: number }> {
  const client = new AnonymizationClient(() => {})
  try {
    const keys = new Set<string>()
    let failed = 0
    for (const file of files) {
      const result = await client.pixelKey(await file.arrayBuffer())
      if (result.ok) keys.add(result.value)
      else failed += 1
    }
    return { keys: [...keys], failed }
  } finally {
    client.close()
  }
}
